/* iter306 — Privacy Dashboard
 *
 * Single surface where a user can see:
 *   • Which consents they have given (health-data processing, age, ToS, privacy)
 *   • Retention timers per data category
 *   • Recent AI decisions that touched their data (GDPR Art. 22 right to explanation)
 *   • Link to the full Privacy Policy + subprocessors matrix
 *
 * Mounted inside SecuritySettings → visible under Profile → Сигурност & Поверителност.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { ShieldCheck, Clock, Sparkles, ExternalLink, CheckCircle2, Loader2 } from "lucide-react";

function formatDate(iso, bg) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(bg ? "bg-BG" : "en-GB", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

const FEATURE_LABELS = {
  lab_analysis: { bg: "Анализ на лабораторни резултати", en: "Lab analysis" },
  conditions_analysis: { bg: "Теми за обсъждане с лекаря", en: "Doctor-discussion topics" },
  digital_twin: { bg: "Digital Twin симулация", en: "Digital Twin simulation" },
  plate_simulator: { bg: "Plate Simulator", en: "Plate Simulator" },
  phenotype: { bg: "Метаболитен тип", en: "Metabolic phenotype" },
  unspecified: { bg: "Друго AI повикване", en: "Other AI call" },
};

const RETENTION_ROWS = [
  { bg: "Активен акаунт", en: "Active account", value: { bg: "до поискване на изтриване", en: "until deletion requested" } },
  { bg: "Здравни данни", en: "Health data", value: { bg: "1 година след изтриване на акаунта", en: "1 year after account deletion" } },
  { bg: "Финансови записи", en: "Financial records", value: { bg: "10 години (счетоводно законодателство)", en: "10 years (accounting law)" } },
  { bg: "AI inference audit log", en: "AI inference audit log", value: { bg: "6 месеца (183 дни)", en: "6 months (183 days)" } },
  { bg: "Временни данни", en: "Temporary tokens", value: { bg: "до 24 часа", en: "up to 24 hours" } },
];

export default function PrivacyDashboard() {
  const { user, API } = useAuth();
  const { language } = useLanguage();
  const bg = language === "bg";
  const [decisions, setDecisions] = useState([]);
  const [total, setTotal] = useState(0);
  const [retentionDays, setRetentionDays] = useState(183);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/me/gdpr/ai-decisions?limit=20`);
        if (cancelled) return;
        setDecisions(Array.isArray(data?.items) ? data.items : []);
        setTotal(Number(data?.total || 0));
        setRetentionDays(Number(data?.retention_days || 183));
      } catch {
        // Fresh accounts may have zero decisions — render empty state.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API]);

  const consents = Array.isArray(user?.consents) ? user.consents : [];
  const latestConsent = consents[consents.length - 1];

  return (
    <div className="space-y-5" data-testid="privacy-dashboard">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={16} className="text-emerald-600" />
          <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">
            {bg ? "Поверителност — Вашата информация" : "Privacy — your information"}
          </h3>
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          {bg
            ? "GDPR Чл. 15, 17, 22 · пълен контрол върху какво събираме, защо и за колко време."
            : "GDPR Art. 15, 17, 22 · full control over what we collect, why, and for how long."}
        </p>
      </div>

      {/* Consent history */}
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800/40 p-3" data-testid="privacy-consents">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 size={14} className="text-emerald-600" />
          <span className="text-[12px] font-semibold text-emerald-900 dark:text-emerald-200">
            {bg ? "Вашите съгласия (GDPR Чл. 7 + 9)" : "Your consents (GDPR Art. 7 + 9)"}
          </span>
        </div>
        {latestConsent ? (
          <div className="space-y-1 text-[11px] text-emerald-900/90 dark:text-emerald-200/90">
            <div className="flex items-start gap-2">
              <span className="shrink-0">✓</span>
              <span>
                {bg ? "Обработка на здравни данни" : "Health-data processing"}
                <span className="opacity-70"> · {formatDate(latestConsent.at, bg)}</span>
              </span>
            </div>
            <div className="flex items-start gap-2">
              <span className="shrink-0">✓</span>
              <span>
                {bg ? "Потвърждение за възраст ≥16 години" : "Age ≥16 confirmation"}
              </span>
            </div>
            <div className="flex items-start gap-2 text-[10px] opacity-80">
              <span className="shrink-0">📜</span>
              <span>
                {bg ? "Приета версия: " : "Version accepted: "}
                <code className="px-1 rounded bg-white/60 dark:bg-black/30">{latestConsent.terms_version || "v1.0"}</code>
                {" · "}
                <code className="px-1 rounded bg-white/60 dark:bg-black/30">{latestConsent.privacy_version || "v1.0"}</code>
              </span>
            </div>
          </div>
        ) : (
          <p className="text-[11px] text-emerald-900/80 dark:text-emerald-200/80 italic">
            {bg
              ? "Няма записани съгласия — вероятно сте се регистрирали преди iter304. Актуалните политики остават приложими."
              : "No consent records yet — you likely registered before iter304. Current policies still apply."}
          </p>
        )}
      </div>

      {/* Retention timers */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3" data-testid="privacy-retention">
        <div className="flex items-center gap-2 mb-2">
          <Clock size={14} className="text-sky-600" />
          <span className="text-[12px] font-semibold text-gray-900 dark:text-gray-100">
            {bg ? "Срокове за съхранение" : "Retention periods"}
          </span>
        </div>
        <div className="space-y-1">
          {RETENTION_ROWS.map((r, i) => (
            <div key={i} className="flex items-start justify-between gap-3 text-[11px]">
              <span className="text-gray-700 dark:text-gray-300">{bg ? r.bg : r.en}</span>
              <span className="text-gray-500 dark:text-gray-400 text-right">{bg ? r.value.bg : r.value.en}</span>
            </div>
          ))}
        </div>
      </div>

      {/* AI decisions */}
      <div className="rounded-xl border border-purple-200 bg-purple-50/40 dark:bg-purple-950/20 dark:border-purple-800/40 p-3" data-testid="privacy-ai-decisions">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-purple-600" />
            <span className="text-[12px] font-semibold text-purple-900 dark:text-purple-200">
              {bg ? "Последни AI решения" : "Recent AI decisions"}
            </span>
          </div>
          <span className="text-[10px] text-purple-700/80 dark:text-purple-300/80">
            {bg ? `Общо: ${total} · ${retentionDays} дни` : `Total: ${total} · ${retentionDays} days`}
          </span>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-[11px] text-purple-700/80">
            <Loader2 size={12} className="animate-spin" />
            <span>{bg ? "Зареждам…" : "Loading…"}</span>
          </div>
        ) : decisions.length === 0 ? (
          <p className="text-[11px] text-purple-900/70 dark:text-purple-200/70 italic">
            {bg
              ? "Все още няма AI анализи по Вашия акаунт. Ще се появят тук автоматично."
              : "No AI analyses on your account yet. They will appear here automatically."}
          </p>
        ) : (
          <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
            {decisions.slice(0, 20).map((d) => {
              const label = FEATURE_LABELS[d.feature_id] || FEATURE_LABELS.unspecified;
              const ok = d.status === "success";
              return (
                <div
                  key={d.run_id}
                  className="flex items-start justify-between gap-2 text-[11px] rounded-lg bg-white/70 dark:bg-black/20 px-2 py-1.5"
                  data-testid={`privacy-ai-row-${d.run_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-purple-900 dark:text-purple-100 truncate">
                      {bg ? label.bg : label.en}
                    </div>
                    <div className="text-[10px] text-purple-700/80 dark:text-purple-300/80 flex flex-wrap gap-x-2">
                      <span>{d.model}</span>
                      <span>·</span>
                      <span>{formatDate(d.created_at, bg)}</span>
                      {d.redaction_applied && <span title={bg ? "Контактна PII беше скрита преди изпращането" : "Contact PII redacted before send"}>🛡</span>}
                    </div>
                  </div>
                  <span
                    className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                      ok
                        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                        : "bg-rose-500/15 text-rose-700 dark:text-rose-300"
                    }`}
                  >
                    {ok ? (bg ? "OK" : "OK") : (bg ? "Грешка" : "Error")}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-2 text-[10px] italic text-purple-800/70 dark:text-purple-300/70">
          {bg
            ? "Съхраняваме само хешове на Вашите prompt-и и изходи — не и самия текст. Данните са GDPR-минимизирани."
            : "We store only hashes of your prompts and outputs — not the raw content. GDPR data-minimised."}
        </p>
      </div>

      <a
        href="/legal#ai-methodology"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-600 hover:underline dark:text-sky-400"
        data-testid="privacy-policy-link"
      >
        <ExternalLink size={12} />
        {bg ? "Политика за поверителност · подизпълнители · Вашите права" : "Privacy policy · subprocessors · your rights"}
      </a>
    </div>
  );
}
